// EditarAnuncioMkt.js
import React, { useEffect, useMemo, useState } from 'react';
import {
    Button,
    Card,
    Col,
    Row,
    Space,
    Typography,
    message,
    Tag,
    DatePicker,
    Input,
    Checkbox,
    InputNumber,
    Spin,
} from 'antd';
import axios from 'axios';
import dayjs from 'dayjs';
import { MoneyInput } from '../../../../Componentes/MoneyInput';
import { useConfigQuery } from '../../../../queries/useConfig';

const { Text, Title } = Typography;

const API_BASE = 'https://apimarket.pedtech.com.br/v1';

const S = (v) => (v == null ? '' : String(v));
const toNumberOrZero = (v) => {
    const n = Number(v);
    return Number.isNaN(n) ? 0 : n;
};
const round2 = (v) => Number(Math.round(toNumberOrZero(v) * 100) / 100);

const formatCurrencyBR = (v) =>
    round2(v).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

const formatCurrencyBRCents = (cents) => formatCurrencyBR((cents || 0) / 100);

export default function EditarAnuncioMkt({ cnpj, voltar, produto, anuncioSelecionado }) {
    const { data: config, error } = useConfigQuery();
    const [saving, setSaving] = useState(false);
    const [isPromo, setIsPromo] = useState(false);
    const [inicioPromo, setInicioPromo] = useState(null); // dayjs
    const [fimPromo, setFimPromo] = useState(null);       // dayjs
    const [precoPromoCents, setPrecoPromoCents] = useState(0);

    const [tipo, setTipo] = useState('CLASSICO');
    const [price, setPrice] = useState(0); // 👈 preço normal EDITÁVEL

    const salesChannelId = S(anuncioSelecionado?.saleschannelid);

    // imagens do produto
    const imagens = useMemo(() => {
        const arr = [];
        if (produto?.img1) arr.push(`https://api.pedtech.com.br/imagens/${produto.img1}`);
        if (produto?.img2) arr.push(`https://api.pedtech.com.br/imagens/${produto.img2}`);
        if (produto?.img3) arr.push(`https://api.pedtech.com.br/imagens/${produto.img3}`);
        if (produto?.img4) arr.push(`https://api.pedtech.com.br/imagens/${produto.img4}`);
        return arr;
    }, [produto]);

    // preenche estado vindo do anúncio
    useEffect(() => {
        if (!anuncioSelecionado) return;

        setTipo(S(anuncioSelecionado?.listingType || 'CLASSICO'));

        const basePrice = round2(anuncioSelecionado.price ?? produto?.preco ?? produto?.price ?? 0);
        setPrice(basePrice);

        const promoPrice =
            anuncioSelecionado?.forsaleprice ??
            anuncioSelecionado?.forSale?.salePrice ??
            null;

        const dStart =
            anuncioSelecionado?.forsaledatestart ??
            anuncioSelecionado?.forSale?.saleDateStart ??
            null;

        const dEnd =
            anuncioSelecionado?.forsaledatestend ??
            anuncioSelecionado?.forSale?.saleDateEnd ??
            null;

        if (promoPrice != null) {
            setIsPromo(true);
            setPrecoPromoCents(Math.round(toNumberOrZero(promoPrice) * 100));
        } else {
            setIsPromo(false);
            setPrecoPromoCents(0);
        }

        setInicioPromo(dStart ? dayjs(dStart) : null);
        setFimPromo(dEnd ? dayjs(dEnd) : null);
    }, [anuncioSelecionado, produto]);

    // promo input “direita => esquerda”
    const handlePrecoPromoKeyDown = (e) => {
        const { key } = e;

        if (['Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(key)) return;

        if (/^[0-9]$/.test(key)) {
            e.preventDefault();
            setPrecoPromoCents((prev) => {
                const base = Number.isFinite(prev) ? prev : 0;
                const next = base * 10 + Number(key);
                if (next > 9999999999) return base;
                return next;
            });
            return;
        }

        if (key === 'Backspace') {
            e.preventDefault();
            setPrecoPromoCents((prev) => Math.floor((Number.isFinite(prev) ? prev : 0) / 10));
            return;
        }

        if (key === 'Delete') {
            e.preventDefault();
            setPrecoPromoCents(0);
            return;
        }

        e.preventDefault();
    };

    // monta payload (igual ao seu padrão)
    const buildPayload = () => {
        const basePrice = round2(price);

        const salesChannel = {
            id: Number(salesChannelId),
            price: basePrice,
            listingType: tipo,
            sellerId: anuncioSelecionado?.sellerId ? String(anuncioSelecionado.sellerId) : '',
        };

        if (isPromo && precoPromoCents > 0 && inicioPromo && fimPromo) {
            const promoPrice = round2(precoPromoCents / 100);
            salesChannel.forSale = {
                salePrice: promoPrice,
                saleDateStart: inicioPromo.toISOString?.() || inicioPromo,
                saleDateEnd: fimPromo.toISOString?.() || fimPromo,
            };
        }

        return {
            productId: S(produto?.id || 'PRODUTO_SEM_ID'),
            productName: S(produto?.nome || produto?.name || ''),
            sku: S(produto?.sku || ''),
            name: S(produto?.nome || produto?.name || ''),
            brand: S(produto?.marca || produto?.brandMkt || 'SEM MARCA'),
            description: S(produto?.descricao || produto?.description || ''),
            price: produto?.preco,
            stock: Number(produto?.quantidade_estoque ?? produto?.stock ?? 1),
            width: Number(produto?.width ?? produto?.largura ?? 1),
            height: Number(produto?.height ?? produto?.altura ?? 1),
            length: Number(produto?.length ?? produto?.comprimento ?? 1),
            weight: Number(produto?.weight ?? produto?.peso ?? 1),
            active: produto?.ativo != null ? Boolean(produto.ativo) : true,

            model: S(produto?.modelo || produto?.model || 'Padrão'),
            anatelCode: S(produto?.anatelCode || ''),

            customCategory: {
                alternativeId: S(produto?.categoria?.id || ''),
            },

            salesChannels: [salesChannel],
            images: imagens,
            metafields: [
                { key: 'cor', value: 'preto' },
                { key: 'Tipos de bancos', value: 'Bebê conforto' },
                { key: 'Normas de segurança', value: 'ECE R44' },
                { key: 'Nome do desenho', value: 'desenho' },
            ],
        };
    };

    function extractErrorMessages(data) {
        if (!data?.error_messages) return null;

        const mensagens = data.error_messages
            .flatMap((item) => item.message || [])
            .map((m) => (m.field ? `${m.field}: ${m.message}` : m.message || String(m)));

        return mensagens;
    }
    const salvarAlteracoes = async () => {
        if (!salesChannelId) {
            message.error('salesChannelId não encontrado no anúncio.');
            return;
        }

        setSaving(true);
        const payload = buildPayload();

        try {
            const headers = { token: config.tokenMarketplace };
            const url = `${API_BASE}/produtos/receberProduto/${cnpj}`;
            const resp = await axios.post(url, payload, { headers });

            if (resp.status >= 200 && resp.status < 300) {
                message.success('Anúncio atualizado com sucesso!');
                voltar();
            } else {
                message.warning(`Resposta inesperada: ${resp.status}`);
            }
        } catch (err) {
            console.error(err);
            // Uso no catch:
            if (err.status === 400) {
                const msgs = extractErrorMessages(err?.response?.data);
                if (msgs && msgs.length) {
                    message.error(msgs.join('\n'));
                    return;
                }
            }
            const detail = err?.response?.data?.message || err?.message || 'Falha ao atualizar anúncio.';
            message.error(detail);
        } finally {
            setSaving(false);
        }
    };

    if (!anuncioSelecionado) {
        return (
            <Card>
                <Text type="secondary">Nenhum anúncio selecionado para edição.</Text>
                <br />
                <Button onClick={voltar}>Voltar</Button>
            </Card>
        );
    }

    return (
        <div style={styles.wrap}>
            <Row gutter={[16, 16]} style={{ height: '100%' }}>
                <Col span={24} style={{ height: '100%' }}>
                    <Card
                        title={(
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Space>
                                    <Button type="primary" onClick={voltar} style={{ background: '#000', borderColor: '#000' }}>
                                        Voltar
                                    </Button>
                                    <Space direction="vertical" size={0}>
                                        <Title level={5} style={{ margin: 0 }}>Editar anúncio</Title>
                                        <Text type="secondary">
                                            Canal: {anuncioSelecionado.saleschannelname || salesChannelId}
                                        </Text>
                                    </Space>
                                </Space>

                                <Button
                                    type="primary"
                                    style={{ background: '#000', borderColor: '#000' }}
                                    onClick={salvarAlteracoes}
                                    loading={saving}
                                >
                                    Salvar alterações
                                </Button>
                            </div>
                        )}
                        bodyStyle={{ padding: 12 }}
                        style={{ height: '100%' }}
                    >
                        <Space direction="vertical" style={{ width: '100%' }} size={12}>
                            <Text strong>{S(produto?.nome || produto?.name || produto?.id)}</Text>

                            <div>
                                <Tag color="blue">
                                    <b>{anuncioSelecionado.saleschannelname || 'Canal'}: {salesChannelId}</b>
                                </Tag>
                                <Tag>
                                    <b>Categoria: {produto?.categoria?.nome || produto?.categoria?.id}</b>
                                </Tag>
                            </div>

                            {/* Tipo */}
                            <div>
                                <b>Tipo:</b>{' '}
                                <Input
                                    value={tipo}
                                    onChange={(e) => setTipo(e.target.value)}
                                    style={{ maxWidth: 200 }}
                                    disabled
                                />
                            </div>

                            {/* Preço normal (EDITÁVEL) */}
                            <div>
                                <b>Preço de venda:</b>
                                <MoneyInput
                                    className="input-estilizado"
                                    value={price}
                                    prefix="R$"
                                    onChange={setPrice}
                                    style={{
                                        width: 100,
                                        marginLeft: 10,
                                        height: 25,                 // mesma altura padrão do Ant Input
                                        padding: '4px 11px',
                                        fontSize: 14,
                                        lineHeight: '22px',
                                        color: 'rgba(0,0,0,0.88)',
                                        backgroundColor: '#fff',
                                        border: '1px solid #d9d9d9',
                                        borderRadius: 6,
                                        outline: 'none',
                                        boxShadow: 'none',
                                        transition: 'all .2s',
                                    }}
                                />
                            </div>

                            {/* Promo */}
                            <Checkbox checked={isPromo} onChange={(e) => setIsPromo(e.target.checked)}>
                                Cadastrar preço promocional
                            </Checkbox>

                            {isPromo && (
                                <div style={{ border: '1px solid #ddd', padding: 10, borderRadius: 6 }}>
                                    <b>Preço Promocional:</b>
                                    <Input
                                        style={{ width: '100%', marginTop: 4 }}
                                        addonBefore="R$"
                                        value={formatCurrencyBRCents(precoPromoCents)}
                                        inputMode="numeric"
                                        onKeyDown={handlePrecoPromoKeyDown}
                                        onPaste={(e) => e.preventDefault()}
                                    />

                                    <br /><br />

                                    <b>Início da Promoção:</b>
                                    <DatePicker
                                        style={{ width: '100%', marginTop: 4 }}
                                        format="DD/MM/YYYY"
                                        value={inicioPromo}
                                        onChange={setInicioPromo}
                                    />

                                    <br /><br />

                                    <b>Fim da Promoção:</b>
                                    <DatePicker
                                        style={{ width: '100%', marginTop: 4 }}
                                        format="DD/MM/YYYY"
                                        value={fimPromo}
                                        onChange={setFimPromo}
                                    />
                                </div>
                            )}
                        </Space>
                    </Card>
                </Col>
            </Row>
        </div>
    );
}

const styles = {
    wrap: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
    },
};
